import { detectCanvasSupport } from "./processing/canvasSupport.js";
import { createImagePipeline } from "./processing/imagePipeline.js";
import { getGameById } from "./core/games.js";
import { createStateStore } from "./ui/stateStore.js";
import { getDomRefs } from "./ui/domRefs.js";
import { bindFormHandlers } from "./ui/formHandlers.js";
import { initAdSlots } from "./ads/adSlots.js";
import { renderCropOverlay, renderImageMeta, renderGameMeta, renderGameOptions, renderPreviewImage, renderScalingMeta, renderStatus } from "./ui/renderStates.js";
import { normalizeScalingMode } from "./core/scaling.js";

const store = createStateStore();
const refs = getDomRefs(document);
const pipeline = createImagePipeline(store);

function render(state) {
    renderStatus(refs, state);
    renderPreviewImage(refs, state.imageBitmap);
    renderCropOverlay(refs, state.crop, state.imageMeta);
    renderImageMeta(refs, state.imageMeta, state.config, state.crop);
    renderScalingMeta(refs, state.imageMeta, state.config, state.crop);
    renderGameMeta(refs, getGameById(state.config.gameId));

    refs.tilesWideInput.value = String(state.config.tilesWide);
    refs.tilesHighInput.value = String(state.config.tilesHigh);
    refs.tileResolutionInput.value = String(state.config.tileResolution);
    if (refs.tileResolutionQuickInput) {
        refs.tileResolutionQuickInput.value = String(state.config.tileResolution);
    }

    const selectedScalingMode = normalizeScalingMode(state.config.scalingMode);
    refs.scalingModeButtons.forEach((button) => {
        const isActive = button.dataset.scalingMode === selectedScalingMode;
        button.setAttribute("aria-pressed", String(isActive));
        button.classList.toggle("secondary", !isActive);
    });
}

function setupCropDrag() {
    let pointerDown = false;
    let previousX = 0;
    let previousY = 0;

    refs.cropOverlay.addEventListener("pointerdown", (event) => {
        pointerDown = true;
        previousX = event.clientX;
        previousY = event.clientY;
        refs.cropOverlay.setPointerCapture(event.pointerId);
    });

    refs.cropOverlay.addEventListener("pointermove", (event) => {
        if (!pointerDown) {
            return;
        }

        const state = store.getState();
        if (!state.crop || !state.imageMeta) {
            return;
        }

        const canvasRect = refs.previewCanvas.getBoundingClientRect();
        const scaleX = state.imageMeta.widthPx / canvasRect.width;
        const scaleY = state.imageMeta.heightPx / canvasRect.height;
        const deltaX = Math.round((event.clientX - previousX) * scaleX);
        const deltaY = Math.round((event.clientY - previousY) * scaleY);
        previousX = event.clientX;
        previousY = event.clientY;

        const nextCrop = {
            ...state.crop,
            x: Math.max(0, Math.min(state.imageMeta.widthPx - state.crop.width, state.crop.x + deltaX)),
            y: Math.max(0, Math.min(state.imageMeta.heightPx - state.crop.height, state.crop.y + deltaY))
        };

        store.setState({ crop: nextCrop, statusMessage: "Crop adjusted." });
    });

    refs.cropOverlay.addEventListener("pointerup", (event) => {
        pointerDown = false;
        refs.cropOverlay.releasePointerCapture(event.pointerId);
    });

    refs.cropOverlay.addEventListener("keydown", (event) => {
        const state = store.getState();
        if (!state.crop || !state.imageMeta) {
            return;
        }

        const step = event.shiftKey ? 10 : 1;
        let nextCrop = state.crop;

        if (event.key === "ArrowUp") {
            nextCrop = { ...state.crop, y: Math.max(0, state.crop.y - step) };
        } else if (event.key === "ArrowDown") {
            nextCrop = { ...state.crop, y: Math.min(state.imageMeta.heightPx - state.crop.height, state.crop.y + step) };
        } else if (event.key === "ArrowLeft") {
            nextCrop = { ...state.crop, x: Math.max(0, state.crop.x - step) };
        } else if (event.key === "ArrowRight") {
            nextCrop = { ...state.crop, x: Math.min(state.imageMeta.widthPx - state.crop.width, state.crop.x + step) };
        } else {
            return;
        }

        event.preventDefault();
        store.setState({ crop: nextCrop, statusMessage: "Crop adjusted with keyboard." });
    });
}

function bootstrap() {
    const support = detectCanvasSupport();
    renderGameOptions(refs, store.getState().config.gameId);
    bindFormHandlers({ refs, store, pipeline });
    setupCropDrag();
    initAdSlots(document);

    if (!support.offscreenSupported) {
        store.setState({ statusMessage: "Running with fallback canvas support." });
    }

    store.subscribe(render);
    render(store.getState());
}

bootstrap();
